import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, MenuItem } from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  autocompleteMenuItemSx,
  autocompleteTextFieldSx,
} from "@components/Autocomplete/Autocomplete.styles.tsx";
import type {
  AutocompleteOption,
  AutocompleteProps,
} from "@components/Autocomplete/Autocomplete.types.ts";
import OverflowTooltip from "@components/OverflowTooltip/OverflowTooltip";
import { VirtualizedListboxComponent } from "./VirtualizedListBox";

const Autocomplete: React.FC<AutocompleteProps> = ({
  options,
  value,
  testId,
  onChange,
  onBlur,
  dir,
  placeholder,
  maxHeight,
  error,
  fullWidth = true,
  disabled,
  inputRef,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<AutocompleteOption | null>(value || null);

  // sync selectedValue with value prop when it changes
  React.useEffect(() => {
    setSelectedValue(value || null);
  }, [value]);

  const [open, setOpen] = useState(false);

  return (
    <MuiAutocomplete
      dir={dir}
      fullWidth={fullWidth}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disablePortal
      disableClearable={!value}
      options={options}
      value={selectedValue}
      disabled={disabled}
      popupIcon={<ExpandMoreIcon />}
      getOptionLabel={(option: AutocompleteOption | string | number) => {
        if (typeof option === "string" || typeof option === "number") {
          return option.toString();
        }
        return option.label?.toString() ?? "";
      }}
      onChange={(_, newValue: AutocompleteOption | null) => {
        setSelectedValue(newValue);
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(_, newValue) => {
        setInputValue(newValue);
      }}
      slots={{ listbox: VirtualizedListboxComponent }}
      slotProps={{
        listbox: {
          style: {
            ...(maxHeight ? { maxHeight: `${maxHeight}px` } : {}),
          },
        },
      }}
      renderInput={(params) => (
        <OverflowTooltip
          title={params.inputProps?.value ?? ""}
          placement="top"
          getTarget={(wrapper) => wrapper.querySelector("input")}
        >
          <TextField
            {...params}
            dir={dir}
            data-testid={testId}
            sx={autocompleteTextFieldSx}
            placeholder={placeholder}
            error={error}
            variant="outlined"
            inputRef={inputRef}
            onBlur={onBlur}
          />
        </OverflowTooltip>
      )}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          key={`${testId}-option-${option.value}`}
          value={option.value}
          sx={autocompleteMenuItemSx}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <OverflowTooltip title={option.label} placement="right">
              {option.label}
            </OverflowTooltip>
          </Box>
        </MenuItem>
      )}
    />
  );
};

export default Autocomplete;
